/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 탭패널의 탭바 엘리먼트를 정의한다.
 *
 * @file /src/components/tab/elements/Tabbar.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@base/Element';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Tab } from './Tab';
import { Panel } from '@components/panel/Panel';
import { Scroller } from '@utils/Scroller';
import { Component } from '@base/Component';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 탭바 위치
     */
    position?: 'top' | 'bottom' | 'hidden';

    /**
     * 탭바 크기
     */
    size?: 'default' | 'small';

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Tabbar extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'tabbar';

    /**
     * 탭바 위치
     */
    position: 'top' | 'bottom' | 'hidden' | null = null;

    /**
     * 탭바 크기
     */
    size: 'default' | 'small' | null = null;

    /**
     * 탭바 스크롤러
     */
    scroller: Scroller = null;

    /**
     * 탭 목록
     */
    tabs: Tab[] = [];

    /**
     * 탭 Dom
     */
    $tabs: Dom = null;

    /**
     * 좌측 이동 버튼 Dom
     */
    $left: Dom = null;

    /**
     * 우측 이동 버튼 Dom
     */
    $right: Dom = null;

    /**
     * 제목 엘리먼트를 정의한다.
     *
     * @param properties - 엘리먼트 초기설정
     */
    constructor(public properties: Properties<Tabbar>) {
        super(properties);

        this.size = this.properties.size ?? 'default';
    }

    /**
     * 부모 컴포넌트를 설정한다.
     *
     * @param component - 현재 엘리먼트를 가진 컴포넌트
     */
    setComponent(component: Component): void {
        super.setComponent(component);
        this.tabs = [];
        for (const item of this.getComponent().getItems() ?? []) {
            const tab = new Tab(item as Panel);
            tab.setComponent(this.getComponent());
            this.tabs.push(tab);
        }
    }

    /**
     * 컨트롤 DOM 을 가져온다.
     *
     * @returns 컨트롤 DOM
     */
    $getTabs(): Dom {
        if (this.$tabs === null) {
            this.$tabs = Html.create('ul');
        }

        return this.$tabs;
    }

    /**
     * 좌측 이동 버튼 DOM 을 가져온다.
     *
     * @returns 좌측 이동 버튼 DOM
     */
    $getLeft(): Dom {
        if (this.$left === null) {
            this.$left = Html.create('button');
            this.$left.setAttr('type', 'button');
            this.$left.html('<i class="mi mi-angle-left"></i>');
            this.$left.on('click', () => {
                const $tabs = this.$getTabs();

                for (let i = this.tabs.length - 1; i >= 0; i--) {
                    const $tab = this.tabs[i].$getElement();
                    const controlStart = $tab.getOffset().left;

                    if (controlStart < $tabs.getOffset().left - 1) {
                        $tab.scrollTo(true, $tabs);
                        return;
                    }
                }
            });
            this.$left.setDisabled(true);
            this.$left.hide();
        }

        return this.$left;
    }

    /**
     * 우측 이동 버튼 DOM 을 가져온다.
     *
     * @returns 우측 이동 버튼 DOM
     */
    $getRigth(): Dom {
        if (this.$right === null) {
            this.$right = Html.create('button');
            this.$right.setAttr('type', 'button');
            this.$right.html('<i class="mi mi-angle-right"></i>');
            this.$right.on('click', () => {
                const $tabs = this.$getTabs();
                const rightEdge = $tabs.getOffset().left + $tabs.getWidth();

                for (let i = 0; i < this.tabs.length; i++) {
                    const $tab = this.tabs[i].$getElement();
                    const tabEnd = $tab.getOffset().left + $tab.getOuterWidth();

                    if (tabEnd > rightEdge + 10) {
                        $tab.scrollTo(true, $tabs);
                        return;
                    }
                }
            });
            this.$right.hide();
        }

        return this.$right;
    }

    /**
     * 스크롤 Element를 가져온다.
     *
     * @param is_rendered - 스크롤바가 정의되지 않은 경우 랜더링할 지 여부
     * @returns 스크롤러
     */
    getScroller(): Scroller {
        if (this.scroller == null) {
            this.scroller = Scroller.get({
                $target: this.$getTabs(),
                $component: this.$getElement(),
                scrollable: 'x',
                listeners: {
                    resize: () => {
                        this.updateScrollState();
                    },
                    update: (me, position) => {
                        this.$getLeft().setDisabled(position.x === 0);
                        this.$getRigth().setDisabled(position.x >= me.getScrollOffset('x'));
                    },
                },
            });
        }

        return this.scroller;
    }

    /**
     * 특정탭을 활성화한다.
     *
     * @param id - 활성화할 탭 고유값 또는 탭 인덱스
     */
    active(id: string): void {
        for (let tab of this.tabs ?? []) {
            if (tab.getTabId() == id) {
                tab.setActive(true);
            } else {
                tab.setActive(false);
            }
        }
    }

    /**
     * 탭을 추가한다.
     *
     * @param item - 추가할 컴포넌트
     * @param position - 추가할 위치 (정의되지 않은 경우 제일 마지막 위치)
     */
    async append(item: Panel, position: number = undefined): Promise<void> {
        const tab = new Tab(item);
        tab.setComponent(this.getComponent());

        if (position === undefined || position >= (this.tabs.length ?? 0)) {
            this.tabs.push(tab);
        } else if (position < 0 && Math.abs(position) >= (this.tabs.length ?? 0)) {
            this.tabs.unshift(tab);
        } else {
            this.tabs.splice(position, 0, tab);
        }

        if (this.isRendered() == true) {
            const $tabs = this.$getTabs();
            $tabs.append(tab.$getElement());
            await tab.render();

            this.updateScrollState();
        }
    }

    /**
     * 탭패널의 자식이 제거되었을 경우 탭바의 탭도 제거한다.
     *
     * @param item - 제거된 탭
     */
    async detach(item: Panel): Promise<void> {
        if (this.isRendered() == true) {
            for (const tab of this.tabs) {
                if (tab.getTabId() == item.getId()) {
                    tab.remove();
                }
            }
        }
    }

    /**
     * 스크롤 가능 여부를 판단하고 이동 버튼을 추가/삭제한다.
     */
    updateScrollState(): void {
        if (this.getScroller().isScrollable('x') == true) {
            this.$getLeft().show();
            this.$getRigth().show();

            /**
             * 버튼이 새로 나타나면서 탭 목록의 가용너비가 줄어들어,
             * 활성화된 탭이 버튼에 가려질 수 있으므로 다시 스크롤위치를 조정한다.
             */
            for (const tab of this.tabs) {
                if (tab.isActive() == true) {
                    tab.scrollTo();
                    break;
                }
            }
        } else {
            this.$getLeft().hide();
            this.$getRigth().hide();
        }
    }

    /**
     * 탭 목록의 현재 상태를 저장한다.
     */
    async storeTabs(): Promise<void> {
        if (this.isRendered() == false) {
            return;
        }

        const tabs = [];
        for (const $tab of Html.all('li', this.$getTabs()).doms) {
            const tab = $tab.getData('tab') as Tab;
            const movable = tab?.movable ?? false;
            if (movable == true) {
                tabs.push({
                    name: tab.getPanel().getName() ?? null,
                    title: await tab.getText(),
                    icon: tab.getIcon(),
                });
            }
        }

        await this.getComponent().fireEvent('sorter', [tabs]);
    }

    /**
     * 가져올 탭 패널 고유값과 일치하는 탭을 가져온다.
     *
     * @param id - 가져올 탭 패널 고유값 또는 탭 패널 인덱스
     * @returns 탭
     */
    getTab(id: string | number): Tab {
        for (const $tab of Html.all('li', this.$getTabs()).doms) {
            const tab = $tab.getData('tab') as Tab;
            if (tab.getPanel().getId() == id) {
                return tab;
            }
        }

        return null;
    }

    /**
     * 컨트롤을 렌더링한다.
     */
    private async renderTabs(): Promise<void> {
        const $tabs = this.$getTabs();
        for (const tab of this.tabs) {
            $tabs.append(tab.$getElement());
            await tab.render();
        }
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        const $bar = this.$getElement();
        $bar.addClass(this.size);

        const $fragment: DocumentFragment = document.createDocumentFragment();

        await this.renderTabs();
        $fragment.append(this.$getTabs().getEl());

        $fragment.append(this.$getLeft().getEl());
        $fragment.append(this.$getRigth().getEl());

        $bar.append($fragment);

        this.getScroller().render();
        this.updateScrollState();

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (removeDom == true) {
            this.$tabs?.remove();
            this.$left?.remove();
            this.$right?.remove();
        }

        super.remove(removeDom);
    }
}
